import { useEffect, useMemo, useRef, useState } from "react";
import { useI18n, LANGUAGES } from "@/i18n";
import { Globe, Search, Check, ChevronDown } from "lucide-react";

export default function LangSwitcher() {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [alignRight, setAlignRight] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const current = LANGUAGES.find((l) => l.code === lang);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    const tm = setTimeout(() => inputRef.current?.focus(), 60);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      clearTimeout(tm);
    };
  }, [open]);

  const items = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return LANGUAGES;
    return LANGUAGES.filter((l) => l.label.toLowerCase().includes(s) || l.code.toLowerCase().includes(s));
  }, [q]);

  const toggle = () => {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setAlignRight(r.left > window.innerWidth / 2);
      setOpenUp(r.bottom + 360 > window.innerHeight);
      setQ("");
    }
    setOpen((o) => !o);
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button type="button" onClick={toggle} className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer" data-testid="lang-switcher">
        <Globe className="w-4 h-4 text-muted-foreground" />
        <span>{current?.label || lang}</span>
        <ChevronDown className={`w-3.5 h-3.5 opacity-60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`absolute z-[100] w-60 rounded-xl bg-white text-[#16233f] shadow-2xl border border-black/10 overflow-hidden ${alignRight ? "right-0" : "left-0"} ${openUp ? "bottom-full mb-2" : "top-full mt-2"}`} data-testid="lang-menu">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-black/10 bg-[#f7f4ee]">
            <Search className="w-4 h-4 text-[#8a8378]" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("langSearchPh")}
              className="w-full bg-transparent text-sm outline-none text-[#16233f] placeholder:text-[#a09a8e]"
              data-testid="lang-search-input"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {items.length === 0 && <div className="px-4 py-3 text-sm text-[#a09a8e]">···</div>}
            {items.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between hover:bg-[#f3efe6] transition-colors ${l.code === lang ? "font-semibold text-[#0D1F3D]" : ""}`}
                data-testid={`lang-option-${l.code}`}
              >
                {l.label}
                {l.code === lang && <Check className="w-4 h-4" style={{ color: "#C5A880" }} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
